// src/hooks/useOfflineDetection.ts
// Tracks navigator.onLine with online/offline events

import { useState, useEffect } from "react";

export interface OfflineState {
  isOnline: boolean;
  isOffline: boolean;
  /** Timestamp (ms) when device last went offline, null if currently online */
  offlineSince: number | null;
}

export function useOfflineDetection(): OfflineState {
  const [isOnline, setIsOnline] = useState<boolean>(
    typeof navigator !== "undefined" ? navigator.onLine : true,
  );
  const [offlineSince, setOfflineSince] = useState<number | null>(null);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setOfflineSince(null);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setOfflineSince(Date.now());
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Sync initial state in case it changed before mount
    if (!navigator.onLine) {
      setIsOnline(false);
      setOfflineSince((prev) => prev ?? Date.now());
    }

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  return { isOnline, isOffline: !isOnline, offlineSince };
}
